// ─────────────────────────────────────────────────────────────────────────────
// wrestlerUtils.js
// Utilitaires centralisés pour la gestion des noms et données des superstars
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Retourne le nom d'un wrestler selon le format demandé.
 *
 * Formats disponibles :
 *  - 'full'     → Nom complet : "Stone Cold Steve Austin" (fallback sur w.name)
 *  - 'display'  → Nom d'affichage principal : nick_name si existe, sinon first + last
 *                 ex: "Stone Cold" / "Randy Orton" / "The Rock"
 *  - 'short'    → Nom court : last_name si existe, sinon display
 *                 ex: "Orton" / "Austin" / "The Rock"
 *  - 'first'    → Prénom uniquement : "Randy" / "Steve" / "The Rock"
 *  - 'formal'   → Prénom + Nom sans surnom : "Steve Austin" / "Randy Orton"
 *  - 'nickname' → Surnom uniquement si existe, sinon display : "Stone Cold" / "Randy Orton"
 *
 * @param {Object} w - Objet wrestler
 * @param {'full'|'display'|'short'|'first'|'formal'|'nickname'} format
 * @returns {string}
 */
export const getWrestlerName = (w, format = 'full') => {
  if (!w) return '?';

  const nick     = w.nick_name    || '';
  const first    = w.first_name   || '';
  const middle   = w.middle_name  || '';
  const last     = w.last_name    || '';
  const fallback = w.name         || '?';

  switch (format) {

    case 'full': {
      // Nom complet avec tous les composants
      // Si les nouveaux champs sont vides → fallback sur w.name
      const parts = [first, middle, last].filter(Boolean);
      if (parts.length === 0) return fallback;
      const fullBuilt = parts.join(' ');
      // Si nick_name existe et est différent du first_name, on le préfixe
      if (nick && nick !== first) return `${nick} ${fullBuilt}`;
      return fullBuilt;
    }

    case 'display': {
      // Nom d'affichage principal : nick + first + last
      const parts = [nick, first, last].filter(Boolean);
      if (parts.length > 0) return parts.join(' ');
      return fallback;
    }

    case 'short': {
      // Nom court pour les rivalités, titres, tableaux
      // Priorité : last_name > nick_name > first_name > fallback
      if (last) return last;
      if (nick) return nick;
      if (first) return first;
      return fallback;
    }

    case 'first': {
      // Prénom uniquement
      if (first) return first;
      if (nick) return nick;
      return fallback;
    }

    case 'formal': {
      // Prénom + Nom, sans surnom (pour les résultats officiels)
      if (first && last) return `${first} ${last}`;
      if (first) return first;
      return fallback;
    }

    case 'nickname': {
      // Surnom si existe, sinon display
      if (nick) return nick;
      if (first && last) return `${first} ${last}`;
      if (first) return first;
      return fallback;
    }

    default:
      return fallback;
  }
};

/**
 * Retourne le nom en majuscules — wrapper pratique pour l'affichage UI.
 * @param {Object} w
 * @param {'full'|'display'|'short'|'first'|'formal'|'nickname'} format
 * @returns {string}
 */
export const getWrestlerNameUpper = (w, format = 'display') => {
  const name = getWrestlerName(w, format);
  return name ? name.toUpperCase() : '';
};

/**
 * Recherche textuelle sur un wrestler — vérifie tous les champs de nom.
 * Utile pour les filtres / barres de recherche.
 * @param {Object} w
 * @param {string} query
 * @returns {boolean}
 */
export const wrestlerMatchesSearch = (w, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (w.name        || '').toLowerCase().includes(q) ||
    (w.nick_name   || '').toLowerCase().includes(q) ||
    (w.first_name  || '').toLowerCase().includes(q) ||
    (w.middle_name || '').toLowerCase().includes(q) ||
    (w.last_name   || '').toLowerCase().includes(q)
  );
};

/**
 * Trie une liste de wrestlers par nom (format display).
 * @param {Object[]} wrestlers
 * @param {'asc'|'desc'} direction
 * @returns {Object[]}
 */
export const sortWrestlersByName = (wrestlers, direction = 'asc') => {
  return [...wrestlers].sort((a, b) => {
    const na = getWrestlerName(a, 'display');
    const nb = getWrestlerName(b, 'display');
    return direction === 'asc'
      ? na.localeCompare(nb)
      : nb.localeCompare(na);
  });
};
